// src/features/audittrail/DsarTab.tsx
// Handle GDPR data subject access requests end to end. ListDataSubjectAccessRequests,
// CreateDataSubjectAccessRequest, GetDataSubjectAccessRequest, ProcessDataSubjectAccessRequest.

import { useCallback, useEffect, useState } from 'react';
import { observer } from '@react';
import { useI18n } from '@react/hooks';
import { audittrailStore } from '@core/stores';
import { Button, Badge, Table, TableHeader, TableBody, TableRow, TableHead, TableCell, TableEmpty, ErrorAlert } from '@react/components';
import type { DataSubjectAccessRequest } from '@core/types/audittrail';
import { DsarFormDialog } from './DsarFormDialog';
import { ProcessDsarDialog } from './ProcessDsarDialog';
import { enumKey, fmtDate, errMessage } from './logic';

export const DsarTab = observer(() => {
  const { t, locale } = useI18n();
  const [items, setItems] = useState<DataSubjectAccessRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [selected, setSelected] = useState<DataSubjectAccessRequest | null>(null);
  const [outcome, setOutcome] = useState<'DSAR_STATUS_COMPLETED' | 'DSAR_STATUS_REJECTED' | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    audittrailStore.ListDataSubjectAccessRequests()
      .then((res) => setItems(res.items?.items ?? []))
      .catch((e: unknown) => setError(errMessage(e, t('audittrail.dsar.loadError'))))
      .finally(() => setLoading(false));
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const openRow = async (row: DataSubjectAccessRequest) => {
    if (!row.entity?.id) return;
    try {
      const res = await audittrailStore.GetDataSubjectAccessRequest(row.entity.id);
      setItems((prev) => prev.map((r) => (r.entity?.id === row.entity?.id ? (res.request ?? r) : r)));
    } catch {
      // ignore
    }
  };

  const statusVariant = (s?: string) => (s === 'DSAR_STATUS_COMPLETED' ? 'success' : s === 'DSAR_STATUS_REJECTED' ? 'error' : s === 'DSAR_STATUS_IN_PROGRESS' ? 'primary' : 'secondary');
  const pending = (s?: string) => s === 'DSAR_STATUS_RECEIVED' || s === 'DSAR_STATUS_IN_PROGRESS';

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">{t('audittrail.dsar.subtitle')}</p>
        <Button size="sm" onClick={() => setFormOpen(true)}>{t('audittrail.dsar.add')}</Button>
      </div>

      {error && <ErrorAlert error={error} dismissible onDismiss={() => setError(null)} />}

      <Table hoverable>
        <TableHeader>
          <TableRow>
            <TableHead>{t('audittrail.dsar.column.subject')}</TableHead>
            <TableHead>{t('audittrail.dsar.column.type')}</TableHead>
            <TableHead>{t('audittrail.dsar.column.due')}</TableHead>
            <TableHead>{t('audittrail.dsar.column.status')}</TableHead>
            <TableHead />
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading ? (
            <TableRow><TableCell colSpan={5} className="py-8 text-center text-muted-foreground">{t('common.loading')}</TableCell></TableRow>
          ) : items.length === 0 ? (
            <TableEmpty colSpan={5} message={t('audittrail.dsar.empty')} />
          ) : (
            items.map((r) => (
              <TableRow key={r.entity?.id} onClick={() => openRow(r)}>
                <TableCell className="text-sm">{r.subject_name} · {r.subject_email}</TableCell>
                <TableCell className="text-sm">{t(`audittrail.dsarType.${enumKey(r.request_type, 'DSAR_TYPE_')}`)}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{fmtDate(r.due_date as unknown as string, locale)}</TableCell>
                <TableCell><Badge variant={statusVariant(r.status)} size="sm">{t(`audittrail.dsarStatus.${enumKey(r.status, 'DSAR_STATUS_')}`)}</Badge></TableCell>
                <TableCell>
                  {pending(r.status) && (
                    <div className="flex justify-end gap-1">
                      <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); setSelected(r); setOutcome('DSAR_STATUS_COMPLETED'); }}>{t('audittrail.dsar.complete')}</Button>
                      <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); setSelected(r); setOutcome('DSAR_STATUS_REJECTED'); }}>{t('audittrail.dsar.reject')}</Button>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      <DsarFormDialog open={formOpen} onClose={() => setFormOpen(false)} onCreated={load} />
      <ProcessDsarDialog
        open={!!selected && !!outcome}
        request={selected}
        outcome={outcome}
        onClose={() => { setSelected(null); setOutcome(null); }}
        onProcessed={load}
      />
    </div>
  );
});

export default DsarTab;
